/* ===== src/utils/generateKindleEpub.ts ===== */
import { apiRequest } from '../../services/api';
import { ProjectState } from '../types';

/**
 * Generate Kindle-optimized EPUB for Amazon KDP publishing
 * This function creates a simplified, reflowable EPUB suitable for Kindle devices
 */
export async function generateKindleEpub(
  projectState: ProjectState,
  selectedSections: any = { 
    frontMatter: { enabled: true }, 
    chapters: { enabled: true }, 
    backMatter: { enabled: true } 
  }
): Promise<Blob> {
  try {
    console.log('📚 [KINDLE-CLIENT] Generating Kindle EPUB for project:', projectState.projectId);
    
    const response = await apiRequest('/api/export/kindle', 'POST', {
      projectId: projectState.projectId,
      selectedSections
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Kindle export failed with status: ${response.status}`);
    }

    const kindleEpubBlob = await (response as any).blob();
    
    console.log('✅ [KINDLE-CLIENT] Successfully generated Kindle EPUB:', {
      size: kindleEpubBlob.size,
      type: kindleEpubBlob.type
    });
    
    return kindleEpubBlob;
  } catch (error) {
    console.error('❌ [KINDLE-CLIENT] Kindle EPUB generation error:', error);
    throw error;
  }
}

/**
 * Download Kindle EPUB file with proper filename
 */
export function downloadKindleEpub(blob: Blob, projectState: ProjectState): void {
  try {
    const title = projectState.blueprint?.worldBuildingSuggestions?.selectedStoryTitle ||
                 projectState.currentProjectName || 
                 'My Work';
    
    const filename = title.replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_');
    const kindleFilename = `${filename}_Kindle.epub`;
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = kindleFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('📥 [KINDLE-CLIENT] Kindle EPUB download initiated:', kindleFilename);
  } catch (error) {
    console.error('❌ [KINDLE-CLIENT] Download error:', error);
    throw error;
  }
}

/**
 * Generate and download Kindle EPUB in one step
 */
export async function generateAndDownloadKindleEpub(
  projectState: ProjectState,
  selectedSections?: any
): Promise<void> {
  try {
    const kindleEpubBlob = await generateKindleEpub(projectState, selectedSections);
    downloadKindleEpub(kindleEpubBlob, projectState);
  } catch (error) {
    console.error('❌ [KINDLE-CLIENT] Generate and download error:', error);
    throw error;
  }
}